import { useCallback } from 'react';
import { llm } from '@cotera/api';
import { useEnabledFeatureFlags } from './use-feature-flag';

/**
 * Model choices that are hidden from the picker UI until their WorkOS feature
 * flag is enabled for the org. This is the single source of truth for
 * flag-gated models — to gate a new one, add a row here mapping its model id to
 * the flag name. Every picker filters through {@link useGatedModelFilter}, so a
 * new row takes effect everywhere at once.
 */
export const GATED_MODEL_FLAGS: Readonly<Record<string, string>> = {
  [llm.LlmModels.lunaRoute.Glm5Dot3Vision]: 'cotera-unlimited',
};

/** The flag gating a model id, or `undefined` when the model is never gated. */
export const gatingFlagForModel = (id: string): string | undefined =>
  GATED_MODEL_FLAGS[id];

/**
 * Returns a predicate for filtering models out of a picker. A model is kept
 * unless it is flag-gated ({@link GATED_MODEL_FLAGS}) and that flag is not
 * enabled for the org. Accepts either a model id or any object carrying one, so
 * it drops straight into the various picker shapes. The predicate is stable
 * across renders except when the enabled-flag set changes, so it is safe to
 * list in `useMemo`/`useCallback` dependency arrays.
 */
export function useGatedModelFilter(): (
  model: string | { id: string }
) => boolean {
  const enabledFlags = useEnabledFeatureFlags();
  return useCallback(
    (model: string | { id: string }): boolean => {
      const id = typeof model === 'string' ? model : model.id;
      const flag = gatingFlagForModel(id);
      return flag === undefined || enabledFlags.has(flag);
    },
    [enabledFlags]
  );
}
